import * as THREE from 'three';
import { CONNECTOR_RIPPLE_SEGMENTS } from './_data/connectorRipple.js';
import { resolveConnectorRippleOffset } from './_pure/connectorRipple.js';
export function createConnectorRippleResources(intervalCount = 1) {
    const geometry = new THREE.BufferGeometry();
    const size = (CONNECTOR_RIPPLE_SEGMENTS + intervalCount * 2 + 1) * 6;
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(size), 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(size), 3).setUsage(THREE.DynamicDrawUsage));
    const material = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true });
    return {
        geometry, material,
        dispose() {
            geometry.dispose();
            material.dispose();
        },
    };
}
export function updateConnectorRippleResources(resources, args) {
    const { activation } = args;
    const ripple = activation.ripple;
    if (!ripple || !Number.isFinite(ripple.amplitude) || ripple.amplitude < 0
        || !Number.isFinite(ripple.pulseLength) || ripple.pulseLength <= 0
        || !Number.isFinite(ripple.oscillationCount) || ripple.oscillationCount <= 0
        || !Number.isFinite(activation.progress01)
        || ![...args.start, ...args.end].every(Number.isFinite)) {
        throw new Error('Invalid connector ripple geometry or progress.');
    }
    const dx = args.end[0] - args.start[0];
    const dy = args.end[1] - args.start[1];
    const dz = args.end[2] - args.start[2];
    const length = Math.hypot(dx, dy, dz);
    const progress = Math.max(0, Math.min(1, activation.progress01));
    const reverse = activation.direction === 'to-from';
    const front = reverse ? 1 - progress : progress;
    const idleColor = new THREE.Color(args.color);
    const activeColor = new THREE.Color(activation.color);
    const positions = resources.geometry.getAttribute('position');
    const colors = resources.geometry.getAttribute('color');
    let vertex = 0;
    const emitVertex = (fraction, color) => {
        positions.setXYZ(vertex, args.start[0] + dx * fraction, args.start[1] + dy * fraction + resolveConnectorRippleOffset({ fraction, length, activation }), args.start[2] + dz * fraction);
        colors.setXYZ(vertex, color.r, color.g, color.b);
        vertex++;
    };
    const emit = (from, to) => {
        const middle = (from + to) / 2;
        const color = (reverse ? middle >= front : middle <= front) ? activeColor : idleColor;
        emitVertex(from, color);
        emitVertex(to, color);
    };
    for (const [intervalFrom, intervalTo] of args.intervals ?? [[0, 1]]) {
        for (let index = Math.floor(intervalFrom * CONNECTOR_RIPPLE_SEGMENTS); index < Math.ceil(intervalTo * CONNECTOR_RIPPLE_SEGMENTS); index++) {
            const from = Math.max(intervalFrom, index / CONNECTOR_RIPPLE_SEGMENTS);
            const to = Math.min(intervalTo, (index + 1) / CONNECTOR_RIPPLE_SEGMENTS);
            if (front > from && front < to) {
                emit(from, front);
                emit(front, to);
            }
            else
                emit(from, to);
        }
    }
    positions.needsUpdate = true;
    colors.needsUpdate = true;
    resources.geometry.setDrawRange(0, vertex);
    resources.material.opacity = args.opacity;
    resources.material.transparent = args.opacity < 1;
    resources.material.depthTest = args.depthTest;
}
